"use client";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Upload, Modal, Button, message } from "antd";
import {
  CameraOutlined,
  CloseOutlined,
  ProfileOutlined,
  FileTextOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { updateUser } from "../../../../Profile/redux/thunks/userThunks";
import { selectEffectiveUserId } from "../../../../sous-profile/redux/selectors/sousProfileSelectors";
import "./EditSidebar.css";

export default function EditSidebar({ user, avatarSrc, routes, onAvatarChange }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const effectiveUserId = useSelector(selectEffectiveUserId);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const firstName = user?.firstName || user?.first_name || user?.prenom || "";
  const lastName = user?.lastName || user?.last_name || user?.nom || "";
  const fullNameFromParts = `${firstName} ${lastName}`.trim();

  const displayName =
    user?.name ||
    user?.profile?.name ||
    user?.fullName ||
    fullNameFromParts ||
    firstName ||
    lastName ||
    t("editSidebar.defaults.user");

  const phone = user?.phone || t("editSidebar.defaults.phone");
  const email = user?.email;

  const avatar =
    avatarSrc ||
    user?.avatar ||
    user?.avatarUrl ||
    "https://cdn-icons-png.flaticon.com/512/149/149071.png";

  // Handle file selection
  const handleBeforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error(t("editSidebar.messages.onlyImages"));
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error(t("editSidebar.messages.maxSize"));
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target.result);
      setSelectedFile(file);
      setPreviewVisible(true);
    };
    reader.readAsDataURL(file);

    return false; // Prevent auto-upload
  };

  // Full avatar update
  const handleUpdateAvatar = async () => {
    const targetUserId =
      user?.id ||
      user?._id ||
      user?.subprofileId ||
      user?.subProfileId ||
      effectiveUserId;

    if (!selectedFile) {
      message.error(t("editSidebar.messages.noFile"));
      return;
    }

    if (!targetUserId) {
      message.error("User ID is missing.");
      return;
    }

    setUploading(true);

    try {
      const base64String = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const result = await dispatch(
        updateUser({
          userId: targetUserId,
          values: { avatar: base64String },
        })
      ).unwrap();

      message.success(t("editSidebar.messages.avatarUpdated"));

      if (onAvatarChange) {
        await onAvatarChange(result, previewImage);
      }

      handleCancelPreview();
    } catch (error) {
      console.error(error);
      message.error(error || t("editSidebar.messages.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewVisible(false);
    setPreviewImage(null);
    setSelectedFile(null);
  };

  return (
    <aside className="pe-side" aria-label={t("editSidebar.aria.sidebar")}>
      <div className="pe-card">
        <div className="pe-avatar">
          {/* Harmonized avatar fallback: initials with blue background */}
          {(!avatar || avatar === "https://cdn-icons-png.flaticon.com/512/149/149071.png") ? (
            <div className="pe-avatar-fallback">
              {(() => {
                const getInitials = (name) => {
                  if (!name) return "?";
                  const parts = name.trim().split(" ");
                  const first = parts[0]?.[0] || "";
                  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
                  return (first + last).toUpperCase();
                };
                return getInitials(displayName);
              })()}
            </div>
          ) : (
            <img src={avatar} alt={displayName} />
          )}

          <Upload
            name="avatar"
            showUploadList={false}
            beforeUpload={handleBeforeUpload}
            accept="image/*"
            disabled={uploading}
          >
            <button
              type="button"
              className="avatar-change-btn"
              aria-label={t("editSidebar.aria.changeAvatar")}
              disabled={uploading}
            >
              <CameraOutlined />
            </button>
          </Upload>
        </div>

        <div className="pe-info">
          <div className="pe-name" title={displayName}>
            {displayName}
          </div>
          <div className="pe-meta">
            {phone && (
              <span className="pe-line" title={phone}>
                <span className="pe-ellipsis">{phone}</span>
              </span>
            )}
            {email && (
              <span className="pe-line" title={email}>
                <span className="pe-ellipsis">{email}</span>
              </span>
            )}
          </div>
        </div>

        <nav className="pe-menu">
          <NavLink
            to={routes?.general || "/profile/edit/general"}
            className={({ isActive }) => "pe-item" + (isActive ? " is-active" : "")}
          >
            <ProfileOutlined className="pe-ic" />
            <span className="pe-item-txt">{t("editSidebar.menu.general")}</span>
          </NavLink>

          <NavLink
            to={routes?.history || "/profile/edit/general"}
            className={({ isActive }) => "pe-item" + (isActive ? " is-active" : "")}
          >
            <FileTextOutlined className="pe-ic" />
            <span className="pe-item-txt">{t("editSidebar.menu.history")}</span>
          </NavLink>

          <NavLink
            to={routes?.insurance || "/profile/edit/general"}
            className={({ isActive }) => "pe-item" + (isActive ? " is-active" : "")}
          >
            <SafetyOutlined className="pe-ic" />
            <span className="pe-item-txt">{t("editSidebar.menu.insurance")}</span>
          </NavLink>
        </nav>
      </div>

      {/* Modal preview */}
      <Modal
        open={previewVisible}
        title={t("editSidebar.modal.title")}
        onCancel={handleCancelPreview}
        centered
        width={500}
        footer={null}
        closeIcon={<CloseOutlined />}
        className="avatar-preview-modal"
      >
        <div className="avatar-preview-content">
          {previewImage && (
            <div className="preview-image-wrapper">
              <img src={previewImage} alt={t("editSidebar.modal.previewAlt")} className="preview-image" />
            </div>
          )}
          <div className="preview-actions">
            <Button onClick={handleCancelPreview} size="large" disabled={uploading}>
              {t("editSidebar.actions.cancel")}
            </Button>
            <Button
              type="primary"
              onClick={handleUpdateAvatar}
              size="large"
              loading={uploading}
              disabled={uploading}
            >
              {uploading ? t("editSidebar.actions.updating") : t("editSidebar.actions.update")}
            </Button>
          </div>
        </div>
      </Modal>
    </aside>
  );
}

EditSidebar.propTypes = {
  user: PropTypes.object,
  avatarSrc: PropTypes.string,
  routes: PropTypes.object,
  onAvatarChange: PropTypes.func,
};
